import { useState } from 'react';
import { AlertTriangle, RefreshCw, Home, Copy, Check, ChevronDown, ChevronUp, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { formatErrorForClipboard, type AppError } from '@/types/error.types';
import { cn } from '@/lib/utils';

interface ErrorDisplayProps {
  error: AppError;
  recovery?: AppError['recovery'];
  /** Display variant */
  variant?: 'full-page' | 'inline' | 'compact';
  /** Custom className */
  className?: string;
  /** Optional close handler for dismissing the error */
  onClose?: () => void;
}

/**
 * ErrorDisplay Component
 * Displays errors with user-friendly messaging and recovery options
 */
export function ErrorDisplay({ 
  error, 
  recovery, 
  variant = 'full-page',
  className,
  onClose
}: ErrorDisplayProps) {
  const [copied, setCopied] = useState(false);
  const [showTechnical, setShowTechnical] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);

  const handleCopyToClipboard = async () => {
    const errorText = formatErrorForClipboard(error);
    
    try {
      await navigator.clipboard.writeText(errorText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const handleRetry = async () => {
    if (recovery?.onRetry) {
      setIsRetrying(true);
      try {
        await recovery.onRetry();
      } finally {
        setIsRetrying(false);
      }
    }
  };

  const handleReload = () => {
    if (recovery?.onReload) {
      recovery.onReload();
    } else {
      window.location.reload();
    }
  };

  const handleGoHome = () => {
    window.location.href = '/';
  };

  const getSeverityColor = () => {
    switch (error.severity) {
      case 'critical':
        return 'destructive';
      case 'error':
        return 'destructive';
      case 'warning':
        return 'default';
      case 'info':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getSeverityIcon = () => {
    return <AlertTriangle className="h-5 w-5" />;
  };

  // Compact variant for inline errors
  if (variant === 'compact') {
    return (
      <Alert variant={error.severity === 'critical' || error.severity === 'error' ? 'destructive' : 'default'} className={className}>
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription className="flex items-center justify-between gap-4">
          <span>{error.message}</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCopyToClipboard}
            className="shrink-0"
          >
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  // Inline variant for section errors
  if (variant === 'inline') {
    return (
      <Card className={cn('border-destructive', className)}>
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-destructive/10 p-2 text-destructive">
                {getSeverityIcon()}
              </div>
              <div>
                <CardTitle className="text-lg">Something went wrong</CardTitle>
                <CardDescription className="mt-1">{error.message}</CardDescription>
              </div>
            </div>
            <Badge variant={getSeverityColor()} className="shrink-0">
              {error.severity}
            </Badge>
          </div>
        </CardHeader>
        
        {error.suggestedActions && error.suggestedActions.length > 0 && (
          <CardContent>
            <div className="space-y-2">
              <p className="text-sm font-medium">Suggested actions:</p>
              <ul className="list-inside list-disc space-y-1 text-sm text-muted-foreground">
                {error.suggestedActions.map((action, index) => (
                  <li key={index}>{action}</li>
                ))}
              </ul>
            </div>
          </CardContent>
        )}
        
        <CardFooter className="flex flex-wrap gap-2">
          {recovery?.canRetry && (
            <Button
              variant="default"
              size="sm"
              onClick={handleRetry}
              disabled={isRetrying}
            >
              <RefreshCw className={cn('mr-2 h-4 w-4', isRetrying && 'animate-spin')} />
              {isRetrying ? 'Retrying...' : 'Try Again'}
            </Button>
          )}
          
          {recovery?.customAction && (
            <Button
              variant="secondary"
              size="sm"
              onClick={recovery.customAction.onClick}
            >
              {recovery.customAction.label}
            </Button>
          )}
          
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopyToClipboard}
          >
            {copied ? (
              <>
                <Check className="mr-2 h-4 w-4" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="mr-2 h-4 w-4" />
                Copy Details
              </>
            )}
          </Button>
        </CardFooter>
      </Card>
    );
  }

  // Full-page variant for critical errors - shown as overlay with backdrop
  return (
    <div className={cn('fixed inset-0 z-[200] flex items-center justify-center p-4 pointer-events-auto', className)}>
      {/* Backdrop overlay - matches Sheet overlay style */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm pointer-events-auto" />
      
      {/* Error card */}
      <Card className="relative w-full max-w-2xl border-destructive shadow-2xl pointer-events-auto">
        {/* Close button in top right - always visible for full-page errors */}
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-4 top-4 h-8 w-8 rounded-full bg-background hover:bg-accent z-10"
          onClick={onClose}
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </Button>
        
        <CardHeader>
          <div className="flex items-start justify-between gap-4 pr-8">
            <div className="flex items-start gap-4">
              <div className="rounded-full bg-destructive/10 p-3 text-destructive">
                {getSeverityIcon()}
              </div>
              <div>
                <CardTitle className="text-2xl">Something went wrong</CardTitle>
                <CardDescription className="mt-2 text-base">{error.message}</CardDescription>
              </div>
            </div>
            <Badge variant={getSeverityColor()} className="shrink-0">
              {error.severity}
            </Badge>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Suggested Actions */}
          {error.suggestedActions && error.suggestedActions.length > 0 && (
            <div className="space-y-3">
              <h3 className="font-semibold">What you can do:</h3>
              <ul className="space-y-2">
                {error.suggestedActions.map((action, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                    <span className="text-sm text-muted-foreground">{action}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <Separator />

          {/* Technical Details (Collapsible) */}
          <div className="space-y-3">
            <button
              onClick={() => setShowTechnical(!showTechnical)}
              className="flex w-full items-center justify-between text-sm font-medium hover:text-primary"
            >
              <span>Technical Details</span>
              {showTechnical ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </button>

            {showTechnical && error.technicalDetails && (
              <div className="space-y-4 rounded-md bg-muted p-4 font-mono text-xs">
                <div className="grid grid-cols-[120px_1fr] gap-2">
                  <span className="text-muted-foreground">Error ID:</span>
                  <span className="break-all">{error.id}</span>
                  
                  <span className="text-muted-foreground">Category:</span>
                  <span>{error.category}</span>
                  
                  <span className="text-muted-foreground">Timestamp:</span>
                  <span>{error.technicalDetails.timestamp}</span>
                  
                  {error.technicalDetails.errorCode && (
                    <>
                      <span className="text-muted-foreground">Error Code:</span>
                      <span>{error.technicalDetails.errorCode}</span>
                    </>
                  )}
                  
                  {error.technicalDetails.statusCode && (
                    <>
                      <span className="text-muted-foreground">HTTP Status:</span>
                      <span>{error.technicalDetails.statusCode}</span>
                    </>
                  )}
                  
                  {error.technicalDetails.endpoint && (
                    <>
                      <span className="text-muted-foreground">Endpoint:</span>
                      <span className="break-all">
                        {error.technicalDetails.method} {error.technicalDetails.endpoint}
                      </span>
                    </>
                  )}
                  
                  {error.technicalDetails.url && (
                    <>
                      <span className="text-muted-foreground">URL:</span>
                      <span className="break-all">{error.technicalDetails.url}</span>
                    </>
                  )}
                </div>
                
                {/* Stack Trace - matches what gets copied to clipboard */}
                {error.technicalDetails.stackTrace && (
                  <div className="space-y-2">
                    <div className="font-semibold text-muted-foreground">STACK TRACE:</div>
                    <div className="max-h-64 overflow-y-auto rounded border border-border bg-background p-3">
                      <pre className="whitespace-pre-wrap break-words text-[10px] leading-relaxed">
                        {error.technicalDetails.stackTrace}
                      </pre>
                    </div>
                  </div>
                )}
                
                {/* User Agent */}
                {error.technicalDetails.userAgent && (
                  <div className="grid grid-cols-[120px_1fr] gap-2 pt-2 border-t border-border">
                    <span className="text-muted-foreground">User Agent:</span>
                    <span className="break-all">{error.technicalDetails.userAgent}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>

        <CardFooter className="flex flex-wrap gap-2">
          {recovery?.canRetry && (
            <Button
              variant="default"
              onClick={handleRetry}
              disabled={isRetrying}
            >
              <RefreshCw className={cn('mr-2 h-4 w-4', isRetrying && 'animate-spin')} />
              {isRetrying ? 'Retrying...' : 'Try Again'}
            </Button>
          )}

          {recovery?.canReload && (
            <Button variant="secondary" onClick={handleReload}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Reload Page
            </Button>
          )}

          {recovery?.canGoHome && (
            <Button variant="secondary" onClick={handleGoHome}>
              <Home className="mr-2 h-4 w-4" />
              Go to Home
            </Button>
          )}

          <Button
            variant="outline"
            onClick={handleCopyToClipboard}
            className="ml-auto"
          >
            {copied ? (
              <>
                <Check className="mr-2 h-4 w-4" />
                Copied to Clipboard!
              </>
            ) : (
              <>
                <Copy className="mr-2 h-4 w-4" />
                Copy Error Details
              </>
            )}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
